import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import MarketplaceHeader from '../../components/navigation/MarketplaceHeader';
import HeroSection from './components/HeroSection';
import ProblemsSection from './components/ProblemsSection';
import TransformSection from './components/TransformSection';
import WhyChooseSection from './components/WhyChooseSection';
import PANIndiaCoverage from './components/PANIndiaCoverage';
import B2BBusinessCategories from './components/B2BBusinessCategories';
import ComprehensiveLeadForm from './components/ComprehensiveLeadForm';

import FeaturedServiceCard from './components/FeaturedServiceCard';
import Icon from '../../components/AppIcon';

import { categoryService } from '../../services/categoryService';
import { serviceService } from '../../services/serviceService';
import {
  trackHomeVisit,
  trackCTAClick,
  trackFeaturedServiceClick,
  trackServiceImpressions,
} from '../../utils/analytics';

const ServiceCatalogHome = () => {
  const [superCategories, setSuperCategories] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadHomeData();
    // Funnel Stage 1: Home visit
    trackHomeVisit({ referrer: document.referrer || 'direct' });
  }, []);

  useEffect(() => {
    if (featuredServices?.length > 0) {
      trackServiceImpressions(featuredServices, 'featured_services_home');
    }
  }, [featuredServices]);

  const loadHomeData = async () => {
    try {
      setLoading(true);
      const [categories, services] = await Promise.all([
      categoryService?.getAll(),
      serviceService?.getFeatured()]
      );
      setSuperCategories(categories || []);
      setFeaturedServices(services || []);
    } catch (err) {
      setError(err?.message);
      console.error('Error loading home data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBrowseServicesClick = () => {
    trackCTAClick('Browse All Services', 'home_bottom_cta');
  };

  const handleBecomePartnerClick = () => {
    trackCTAClick('Become a Partner', 'home_bottom_cta');
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>ZippyAssist - Global Extended Operations Partner | 2000+ Business Services</title>
        <meta name="description" content="ZippyAssist connects UK, US, Canada & UAE businesses with 500+ verified Indian service partners. Save 60% time & 25% cost on Tax, Legal, IT, HR, Digital Marketing and 2000+ intangible services." />
        <meta name="keywords" content="business services marketplace, outsource services India, GST filing, company registration, digital marketing, IT services, legal compliance, payroll management, ZippyAssist" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://zippyassist.in/" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="ZippyAssist - Global Extended Operations Partner | 2000+ Business Services" />
        <meta property="og:description" content="Trusted by international businesses in UK, US, Canada, UAE. Connect with 500+ verified Indian service partners for Tax, Legal, IT, HR & 2000+ intangible services." />
        <meta property="og:url" content="https://zippyassist.in/" />
        <meta property="og:site_name" content="ZippyAssist" />
        <meta property="og:image" content="https://zippyassist.in/assets/images/200x68-1775706855169.jpg" />
        <meta property="og:locale" content="en_IN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="ZippyAssist - Global Extended Operations Partner | 2000+ Business Services" />
        <meta name="twitter:description" content="Connect with 500+ verified Indian service partners for Tax, Legal, IT, HR & 2000+ intangible services. Trusted by UK, US, Canada, UAE businesses." />
        <meta name="twitter:image" content="https://zippyassist.in/assets/images/200x68-1775706855169.jpg" />

        {/* JSON-LD: WebSite */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "ZippyAssist",
          "url": "https://zippyassist.in",
          "description": "Global marketplace connecting international businesses with 500+ verified Indian service partners across 2000+ intangible service categories.",
          "potentialAction": {
            "@type": "SearchAction",
            "target": {
              "@type": "EntryPoint",
              "urlTemplate": "https://zippyassist.in/service-category-browse?q={search_term_string}"
            },
            "query-input": "required name=search_term_string"
          },
          "publisher": {
            "@type": "Organization",
            "name": "EBAS Venture Private Limited",
            "url": "https://zippyassist.in",
            "logo": {
              "@type": "ImageObject",
              "url": "https://zippyassist.in/assets/images/200x68-1775706855169.jpg"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+91-7039149001",
              "contactType": "customer service",
              "areaServed": ["IN", "GB", "US", "CA", "AE"],
              "availableLanguage": "English"
            }
          }
        })}</script>

        {/* JSON-LD: ItemList of service categories */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "ZippyAssist Service Categories",
          "description": "2000+ intangible business services across major categories",
          "url": "https://zippyassist.in/service-category-browse",
          "numberOfItems": 2000,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Tax & Accounting Services", "url": "https://zippyassist.in/service-category-browse?category=tax-accounting" },
            { "@type": "ListItem", "position": 2, "name": "Legal & Compliance", "url": "https://zippyassist.in/service-category-browse?category=legal-compliance" },
            { "@type": "ListItem", "position": 3, "name": "Digital Marketing", "url": "https://zippyassist.in/service-category-browse?category=digital-marketing" },
            { "@type": "ListItem", "position": 4, "name": "IT & Technology", "url": "https://zippyassist.in/service-category-browse?category=it-technology" },
            { "@type": "ListItem", "position": 5, "name": "Professional Services", "url": "https://zippyassist.in/service-category-browse?category=professional-services" },
            { "@type": "ListItem", "position": 6, "name": "HR & Recruitment", "url": "https://zippyassist.in/service-category-browse?category=hr-recruitment" }
          ]
        })}</script>
      </Helmet>

      <MarketplaceHeader />

      <main>
        <HeroSection />
        <ProblemsSection />
        <TransformSection />
        <WhyChooseSection />
        <PANIndiaCoverage />
        <B2BBusinessCategories />

        <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-muted">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 md:mb-12 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                  Top Global Outsource Services
                </h2>
                <p className="text-base md:text-lg text-muted-foreground">
                  Trusted by businesses in UK, US, Canada, UAE - Verified international service providers
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="TrendingUp" size={18} className="text-accent" />
                <span>Updated daily</span>
              </div>
            </div>

            {loading ?
            <div className="text-center py-12">
                <div className="text-muted-foreground">Loading services...</div>
              </div> :

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredServices?.map((service, index) =>
              <div key={service?.id} onClick={() => trackFeaturedServiceClick(service?.name, service?.id, index + 1)}>
                <FeaturedServiceCard service={service} />
              </div>
              )}
              </div>
            }
          </div>
        </section>

        <ComprehensiveLeadForm />

        <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">
              Ready to Find Your Perfect Service Provider?
            </h2>
            <p className="text-base md:text-lg opacity-90 mb-6 md:mb-8">
              Join thousands of satisfied international clients from UK, US, Canada, UAE who found trusted professionals through ZippyAssist Services Mall
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <button onClick={handleBrowseServicesClick} className="px-6 md:px-8 py-3 md:py-4 bg-white text-primary rounded-lg md:rounded-xl font-semibold hover:bg-gray-100 transition-colors text-sm md:text-base">
                Browse All Services
              </button>
              <button onClick={handleBecomePartnerClick} className="px-6 md:px-8 py-3 md:py-4 bg-transparent border-2 border-white text-white rounded-lg md:rounded-xl font-semibold hover:bg-white/10 transition-colors text-sm md:text-base">
                Become a Partner
              </button>
            </div>
          </div>
        </section>
      </main>
      <footer className="bg-card border-t border-border py-8 md:py-12 px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary flex items-center justify-center">
                  <Icon name="ShoppingBag" size={24} color="white" />
                </div>
                <span className="text-xl font-semibold text-foreground">ZippyAssist</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Your trusted marketplace for discovering and connecting with verified service professionals across 2000+ categories.
              </p>
              <div className="flex items-center gap-3">zippyassist@business@obasegroup.com




              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><a href="/service-category-browse" className="text-sm text-muted-foreground hover:text-primary">Browse Services</a></li>
                <li><a href="/digital-service-standee" className="text-sm text-muted-foreground hover:text-primary">Featured Services</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">How It Works</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">Pricing</a></li>
              </ul>
            </div>

            <div>
              <h3 className="text-base font-semibold text-foreground mb-4">Company</h3>
              <ul className="space-y-2">
                <li><a href="/about-ebas-venture" className="text-sm text-muted-foreground hover:text-primary">About Us</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">Contact</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">Privacy Policy</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-primary">Terms of Service</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              &copy; {new Date()?.getFullYear()} ZippyAssist by EBAS Venture Private Limited. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default ServiceCatalogHome;